import React, {
    useCallback, useEffect, useState, useRef
} from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Button, Grid, Box, Link
} from 'components/ui';
import FrictionlessAddressForm from 'components/Addresses/FrictionlessAddressForm';
import Checkbox from 'components/Inputs/Checkbox/Checkbox';
import userUtils from 'utils/User';
import FormsUtils from 'utils/Forms';
import orderUtils from 'utils/Order';
import errorsUtils from 'utils/Errors';
import Debounce from 'utils/Debounce';
import processEvent from 'analytics/processEvent';
import analyticsConsts from 'analytics/constants';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import Storage from 'utils/localStorage/Storage';
import addressUtils from 'utils/Address';
import cookieUtils from 'utils/Cookies';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import { SECTION_NAMES, SHIPPING_ADDRESS_VALIDATION_ERROR } from 'constants/frictionlessCheckout';
import { addressApiCall } from 'services/Checkout';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import analyticsUtils from 'analytics/utils';
import anaConsts from 'analytics/constants';

const { formatZipCode } = addressUtils;

const styles = {
    hidden: { display: 'none' }
};

const selectAddressApiAndPayload = ({
    isNCR, existingAddress, deletedFromAddressBook, orderId, dataToSend, apis
}) => {
    const updatedDataToSend = { ...dataToSend };

    if (isNCR && existingAddress && !deletedFromAddressBook) {
        updatedDataToSend.replacementOrderId = orderId;

        return { apiToCall: apis.updateAddress, dataToSend: updatedDataToSend };
    }

    if (isNCR) {
        delete updatedDataToSend.addressId;
        updatedDataToSend.replacementOrderId = orderId;

        return { apiToCall: apis.shippingAddress, dataToSend: updatedDataToSend };
    }

    if (existingAddress) {
        return { apiToCall: apis.updateShippingAddress, dataToSend: updatedDataToSend };
    }

    return { apiToCall: apis.createShippingAddress, dataToSend: updatedDataToSend };
};

const getCountryFromAddress = (address, shipCountry) => {
    const country = address?.country;

    if (address && country) {
        return country;
    } else {
        return shipCountry;
    }
};

export const useAnalytics = (localization, address, isGiftCardSection, hasUserOpenedAddressForm, isCollapsed, setCheckoutSectionErrors) => {
    const setAnalytics = useCallback(() => {
        const { editShipAddress, addNewShipAddress } = localization;
        const linkName = address ? editShipAddress : addNewShipAddress;
        !isCollapsed && FrictionlessCheckoutBindings.setAddOrEditShippingAddressAnalytics(!!address, linkName, isGiftCardSection, true);
    }, [localization, address, isGiftCardSection, hasUserOpenedAddressForm, isCollapsed]);

    const setAnalyticsAndErrors = (sectionName, errors) => {
        if (Object.keys(errors)?.length > 0) {
            FrictionlessCheckoutBindings.setSectionLevelErrorAnalytics(sectionName, errors[sectionName]);
            setCheckoutSectionErrors(errors);
        }
    };

    const processEvents = () => {
        processEvent.process(analyticsConsts.LINK_TRACKING_EVENT, {
            data: {
                eventStrings: [analyticsConsts.Event.EVENT_71, 'event121'],
                linkName: 'D=c55',
                pageDetail: digitalData.page.pageInfo.pageName,
                actionInfo: `${analyticsConsts.PAGE_TYPES.CHECKOUT}:default shipping address`,
                previousPage: 'checkout:shipping:n/a:*'
            }
        });
    };

    return {
        setAnalytics,
        setAnalyticsAndErrors,
        processEvents
    };
};

function ShipAddressForm(props) {
    const { orderHasPhysicalGiftCard, isGiftCardSection } = props;
    const [isDefault, setIsDefault] = useState(props.isDefaultChecked);
    const [isShortenedLoqateForm, setIsShortenedLoqateForm] = useState(!props.address);
    const [errorMessages, setErrorMessages] = useState([]);

    const addressForm = useRef();
    const shouldHiddenLoqate = useRef(false);
    const shipCountry = userUtils.getShippingCountry().countryCode;
    const { setAnalytics, setAnalyticsAndErrors, processEvents } = useAnalytics(
        props.localization,
        props.address,
        isGiftCardSection,
        props.hasUserOpenedAddressForm,
        props.isCollapsed,
        props.setCheckoutSectionErrors
    );

    const {
        localization: {
            setAsDefaultCheckbox, cancelButton, useThisAddress, enterAddressManually, loqateSelectionError
        },
        children
    } = props;

    useEffect(() => {
        props.togglePlaceOrderDisabled(true);
        setAnalytics();
    }, [setAnalytics, props.togglePlaceOrderDisabled]);

    const closeShipAddressForm = () => {
        shouldHiddenLoqate.current = false;
        setErrorMessages([]);
        props.clearNamedSectionErrors?.([props.sectionName]);
        props.cancelCallback?.();
    };

    const handleIsDefault = () => {
        setIsDefault(!isDefault);

        if (!isDefault) {
            processEvents();
        }
    };

    const showError = (message, value, errorKey) => {
        if (!addressForm.current.handleResponseError(message, value, errorKey)) {
            if (message) {
                FrictionlessCheckoutBindings.setSectionLevelErrorAnalytics(
                    isGiftCardSection ? SECTION_NAMES.GIFT_CARD_DELIVERY : SECTION_NAMES.DELIVER_TO,
                    message
                );
                const errors = FrictionlessUtils.setDeliverToErrors(message, isGiftCardSection);
                props.setCheckoutSectionErrors(errors);
            }

            if (errorMessages.indexOf(message) < 0) {
                setErrorMessages(prevMessages => prevMessages.concat([message]));
            }
        }
    };

    const createAddress = () => {
        const { getData, isAddressVerified, hasAVS, isAddressModified } = addressForm.current;
        const { address, isNCR } = props;
        const _deletedFromAddressBook = address?._deletedFromAddressBook || false;

        const newAddressData = getData();
        const dataToSend = Object.assign({}, newAddressData, {
            isDefaultAddress: isDefault,
            shippingGroupId: props.shippingGroupId,
            addressValidated: hasAVS() ? isAddressVerified() : undefined
        });

        const defaultChanged = Boolean(address?.isDefault) !== Boolean(isDefault);
        const wasModified = typeof isAddressModified === 'function' ? isAddressModified() : true;

        if (!isNCR && address && !wasModified && !defaultChanged && !props.isGuestCheckout) {
            props.validateAddress(dataToSend.address.addressId, dataToSend);

            return;
        }

        const orderId = orderUtils.getOrderId();
        const isSameDayOder = orderUtils.isSdd();
        const { apiToCall, dataToSend: updatedDataToSend } = selectAddressApiAndPayload({
            isNCR,
            existingAddress: address,
            deletedFromAddressBook: _deletedFromAddressBook,
            orderId,
            dataToSend,
            apis: {
                updateAddress: props.updateAddress,
                shippingAddress: props.shippingAddress,
                updateShippingAddress: props.updateShippingAddress,
                createShippingAddress: props.createShippingAddress
            }
        });
        const callback = validateAddress;

        if (apiToCall === props.createShippingAddress && isSameDayOder) {
            const postalCode = formatZipCode(updatedDataToSend.address.postalCode);

            addressApiCall({
                dataToSend: updatedDataToSend,
                apiToCall,
                callback: results => {
                    props
                        .updatePreferredZipCode({ postalCode })
                        .then(zipCodeData => {
                            const zipData = {
                                preferredZipCode: zipCodeData.zipCode,
                                encryptedStoreIds: zipCodeData.encryptedStoreIds
                            };
                            Storage.session.setItem(LOCAL_STORAGE.PREFERRED_ZIP_CODE, zipData);

                            if (Sephora.configurationSettings.setZipStoreCookie) {
                                cookieUtils.write('sameDayZipcodeCookie', zipCodeData.zipCode, null, false, false);
                            }
                        })
                        .catch(error => errorsUtils.collectAndValidateBackEndErrors(error, this));
                    callback(results, updatedDataToSend);
                },
                isNCR,
                setAnalyticsAndErrors,
                comp: { showError }
            });
        } else {
            addressApiCall({
                dataToSend: updatedDataToSend,
                apiToCall,
                callback,
                isNCR,
                setAnalyticsAndErrors,
                comp: { showError }
            });
        }
    };

    const validateAddressForm = Debounce.preventDoubleClick(e => {
        e.preventDefault();
        const email = addressForm.current?.emailInput?.state?.value;

        props.initPaze(email, props.isGuestCheckout);
        FrictionlessCheckoutBindings.thisAddressButtonAnalytics(props.sectionName, isDefault);

        const isValid = addressForm.current.validateForm();

        if (isValid) {
            createAddress();
        } else {
            FrictionlessCheckoutBindings.setSectionLevelErrorAnalytics(props.sectionName);
            const errors = { [props.sectionName]: SHIPPING_ADDRESS_VALIDATION_ERROR };
            props.setCheckoutSectionErrors(errors);
        }
    });

    const validateAddress = (data, uiAddress) => {
        const { addressId } = data;

        props.validateAddress(addressId, uiAddress);

        if (props.isNCR) {
            props.setLastUsedShippingAddressId(addressId);
        }
    };

    const setManualEntry = () => {
        setIsShortenedLoqateForm(false);
    };

    const fireUseManualAddressAnalytics = () => {
        const mostRecentAsyncLoadEvent = analyticsUtils.getMostRecentEvent('asyncPageLoad');
        const actionString = isGiftCardSection
            ? `gift card:${anaConsts.ACTION_INFO.ENTER_MANUAL_ADDRESS}`
            : `deliver to:${anaConsts.ACTION_INFO.ENTER_MANUAL_ADDRESS}`;
        const eventData = {
            data: {
                actionInfo: actionString,
                previousPage: mostRecentAsyncLoadEvent?.eventInfo?.attributes?.previousPageName
            }
        };

        processEvent.process(anaConsts.LINK_TRACKING_EVENT, eventData);
    };

    const setIsManualEntry = () => {
        shouldHiddenLoqate.current = true;
        setManualEntry();
        fireUseManualAddressAnalytics();
    };

    const showCancelButton =
        // Show for SDD no-match users who have saved addresses (cancelCallback = return to address list)
        // !isFirstAddress means user has at least one saved address.
        (props.cancelCallback && !props.isFirstAddress) ||
        // Otherwise: not a first-time address entry, and either non-SDD or SDD with a matching zip
        (!props.isFirstAddress && (!props.isSdd || props.addressByPreferredZipCode >= 1));

    return (
        <>
            <div id='ship_address_form'>
                <FrictionlessAddressForm
                    editStore={FormsUtils.getStoreEditSectionName(FormsUtils.FORMS.CHECKOUT.SHIPPING_ADDRESS, props.isNewUser)}
                    isEditMode={!!props.address}
                    address={props.address}
                    country={getCountryFromAddress(props.address, shipCountry)}
                    isCheckout={true}
                    isGuestCheckout={props.isGuestCheckout}
                    isCountryFieldHidden={true}
                    shouldHiddenLoqate={shouldHiddenLoqate.current}
                    showAddress2Input={true}
                    onRef={comp => {
                        addressForm.current = comp;
                    }}
                    isShortenedLoqateForm={isShortenedLoqateForm}
                    onSubmitLoqateAddress={setManualEntry}
                    orderHasPhysicalGiftCard={orderHasPhysicalGiftCard}
                    isGiftCardSection={isGiftCardSection}
                    loqateSelectionError={loqateSelectionError}
                />
                <div css={(props.isGuestCheckout || isShortenedLoqateForm) && styles.hidden}>
                    <Checkbox
                        checked={isDefault}
                        onClick={handleIsDefault}
                        disabled={props.address?.isDefault || props.isFirstAddress}
                        name='is_default'
                        marginBottom={4}
                    >
                        {setAsDefaultCheckbox}
                    </Checkbox>
                </div>
            </div>

            {isShortenedLoqateForm && (
                <Link
                    color='link'
                    children={enterAddressManually}
                    onClick={setIsManualEntry}
                    marginBottom={4}
                />
            )}

            <Grid
                alignItems='center'
                gridTemplateColumns={props.isFirstAddress ? ['1fr', '1fr 1fr 1fr'] : ['1fr 1fr', '1fr 1fr 2fr']}
                gap={4}
            >
                {showCancelButton && (
                    <Button
                        variant='secondary'
                        onClick={closeShipAddressForm}
                        children={cancelButton}
                        minWidth={true}
                    />
                )}
                <Button
                    variant='primary'
                    children={useThisAddress}
                    onClick={validateAddressForm}
                />
            </Grid>

            {children && <Box marginTop={0}>{children}</Box>}
        </>
    );
}

export default wrapFunctionalComponent(ShipAddressForm, 'ShipAddressForm');
